import React, { Component } from 'react';
import { styled } from '@compiled/react';
import Link from '@atlaskit/link';
import Button from '@atlaskit/button/standard-button';
import { ModalBody, ModalFooter, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, xcss } from '@atlaskit/primitives';
import { Radio } from '@atlaskit/radio';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import type { SingleComponent } from '@atlassian/jira-component-picker/src/types.tsx';
import ComponentPicker from '@atlassian/jira-component-picker/src/view/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { Context } from '../model/index.tsx';
import messages from './messages.tsx';

export type Props = {
	isDialogOpen: boolean;
	isInProgress: boolean;
	context: Context;
	onCancel: () => void;
	onConfirm: (moveIssuesTo: string | undefined) => void;
	intl: IntlShape;
};

type State = {
	shouldSwap: boolean;
	swapComponent: SingleComponent | undefined;
};

// eslint-disable-next-line jira/react/no-class-components
class ViewComponent extends Component<Props, State> {
	state: State = {
		shouldSwap: true,
		swapComponent: undefined,
	};

	onPrimaryClick = () => {
		const moveIssuesTo = this.getMoveIssuesTo();
		this.props.onConfirm(moveIssuesTo);
	};

	onSwapComponentsSelected = () => {
		this.setState({ shouldSwap: true });
	};

	onRemoveComponentFromIssuesSelected = () => {
		this.setState({ shouldSwap: false });
	};

	onSwapComponentChange = (components?: SingleComponent[]) => {
		this.setState({ swapComponent: components && components[0] });
	};

	getMoveIssuesTo(): string | undefined {
		const { shouldSwap, swapComponent } = this.state;

		if (shouldSwap && swapComponent) {
			return swapComponent.id;
		}

		return undefined;
	}

	isDeleteDisabled(): boolean {
		if (this.state.shouldSwap) {
			return !this.state.swapComponent;
		}

		return this.props.isInProgress;
	}

	renderFooter = () => {
		const {
			isInProgress,
			onCancel,
			intl: { formatMessage },
		} = this.props;

		return (
			<ModalFooter>
				<FooterContainer>
					<ButtonsContainer>
						{isInProgress && (
							<Box xcss={spinnerContainerStyles}>
								<Spinner size="small" />
							</Box>
						)}
						<Button appearance="subtle-link" isDisabled={isInProgress} onClick={onCancel}>
							{formatMessage(messages.cancelButton)}
						</Button>
						<Button
							appearance="danger"
							type="submit"
							isDisabled={this.isDeleteDisabled()}
							onClick={this.onPrimaryClick}
						>
							{formatMessage(messages.deleteButton)}
						</Button>
					</ButtonsContainer>
				</FooterContainer>
			</ModalFooter>
		);
	};

	render() {
		const {
			context: { projectKey, id: componentId, relatedIssueCount, relatedIssueUrl, name },
			onCancel,
			isDialogOpen,
			intl: { formatMessage },
		} = this.props;
		const { shouldSwap } = this.state;

		const dialogDescription = (
			// eslint-disable-next-line @atlaskit/design-system/use-primitives-text
			<p>
				{fg('replace-anchor-with-link__morphinecake') ? (
					<Link href={relatedIssueUrl}>
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.deleteComponentWarningLinkIssueTermRefresh
								: messages.deleteComponentWarningLink,
							{ relatedIssueCount },
						)}
					</Link>
				) : (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a href={relatedIssueUrl}>
						{formatMessage(
							fg('jira-issue-terminology-refresh-m3')
								? messages.deleteComponentWarningLinkIssueTermRefresh
								: messages.deleteComponentWarningLink,
							{ relatedIssueCount },
						)}
					</a>
				)}{' '}
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.deleteComponentWillAffectIssuesIssueTermRefresh
						: messages.deleteComponentWillAffectIssues,
					{ relatedIssueCount },
				)}{' '}
				{formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.deleteComponentYouCanChangeIssuesIssueTermRefresh
						: messages.deleteComponentYouCanChangeIssues,
					{ relatedIssueCount },
				)}
			</p>
		);

		if (!isDialogOpen) {
			return null;
		}

		return (
			<form>
				<ShortcutScope>
					<ModalDialog
						messageId="project-directories.components.delete.modal-dialog"
						messageType="transactional"
						width="medium"
						onClose={onCancel}
					>
						<ModalHeader>
							<ModalTitle appearance="danger" isMultiline={false}>
								{formatMessage(messages.headerNew, {
									component: name,
								})}
							</ModalTitle>
						</ModalHeader>
						<ModalBody>
							{dialogDescription}
							<Box xcss={deleteOptionsContainerStyles}>
								<Radio
									name="swap-option-swap"
									isChecked={shouldSwap}
									onChange={this.onSwapComponentsSelected}
									label={formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.changeComponentRadioIssueTermRefresh
											: messages.changeComponentRadio,
									)}
								/>
								<ComponentPickerContainer>
									<ComponentPicker
										projectKeyOrId={projectKey}
										ignore={[componentId]}
										placeholder={formatMessage(messages.componentPickerPlaceholder)}
										onSelected={this.onSwapComponentChange}
									/>
								</ComponentPickerContainer>
								<Radio
									name="swap-option-remove"
									isChecked={!shouldSwap}
									onChange={this.onRemoveComponentFromIssuesSelected}
									label={formatMessage(
										fg('jira-issue-terminology-refresh-m3')
											? messages.removeComponentRadioIssueTermRefresh
											: messages.removeComponentRadio,
									)}
								/>
							</Box>
						</ModalBody>
						{this.renderFooter()}
					</ModalDialog>
				</ShortcutScope>
			</form>
		);
	}
}

export default injectIntl(ViewComponent);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonsContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	alignItems: 'center',
});

const spinnerContainerStyles = xcss({
	marginRight: 'space.200',
});

const deleteOptionsContainerStyles = xcss({
	marginTop: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FooterContainer = styled.div({
	display: 'flex',
	justifyContent: 'flex-end',
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ComponentPickerContainer = styled.div({
	width: '300px',
	marginTop: token('space.050'),
	marginRight: 0,
	marginBottom: token('space.200'),
	marginLeft: token('space.300'),
});
